import type { ThreadId } from "@cmmd-forge/contracts";

import type { Thread } from "../types";

const EMPTY_PROPOSED_PLANS: Thread["proposedPlans"] = [];

export type ThreadPlanCatalogEntry = Pick<Thread, "id" | "proposedPlans">;

export interface ThreadPlanCatalogState {
  readonly environmentStateById: Readonly<
    Record<
      string,
      {
        readonly threadShellById: Readonly<Record<string, object | undefined>>;
        readonly proposedPlanIdsByThreadId: Readonly<Record<string, readonly string[] | undefined>>;
        readonly proposedPlanByThreadId: Readonly<
          Record<string, Record<string, Thread["proposedPlans"][number]> | undefined>
        >;
      }
    >
  >;
}

export function createThreadPlanCatalogSelector(
  threadIds: readonly ThreadId[],
): (state: ThreadPlanCatalogState) => ThreadPlanCatalogEntry[] {
  let previousThreadIds: readonly ThreadId[] = [];
  let previousResult: ThreadPlanCatalogEntry[] = [];
  let previousEntries = new Map<
    ThreadId,
    {
      shell: object | null;
      proposedPlanIds: readonly string[] | undefined;
      proposedPlansById: Record<string, Thread["proposedPlans"][number]> | undefined;
      entry: ThreadPlanCatalogEntry;
    }
  >();

  return (state) => {
    const sameThreadIds =
      previousThreadIds.length === threadIds.length &&
      previousThreadIds.every((id, index) => id === threadIds[index]);
    const nextEntries = new Map<
      ThreadId,
      {
        shell: object | null;
        proposedPlanIds: readonly string[] | undefined;
        proposedPlansById: Record<string, Thread["proposedPlans"][number]> | undefined;
        entry: ThreadPlanCatalogEntry;
      }
    >();
    const nextResult: ThreadPlanCatalogEntry[] = [];
    let changed = !sameThreadIds;

    for (const threadId of threadIds) {
      let shell: object | undefined;
      let proposedPlanIds: readonly string[] | undefined;
      let proposedPlansById: Record<string, Thread["proposedPlans"][number]> | undefined;

      for (const environmentState of Object.values(state.environmentStateById)) {
        const matchedShell = environmentState.threadShellById[threadId];
        if (!matchedShell) {
          continue;
        }
        shell = matchedShell;
        proposedPlanIds = environmentState.proposedPlanIdsByThreadId[threadId];
        proposedPlansById = environmentState.proposedPlanByThreadId[threadId] as
          | Record<string, Thread["proposedPlans"][number]>
          | undefined;
        break;
      }

      if (!shell) {
        const previous = previousEntries.get(threadId);
        if (
          previous &&
          previous.shell === null &&
          previous.proposedPlanIds === undefined &&
          previous.proposedPlansById === undefined
        ) {
          nextEntries.set(threadId, previous);
          continue;
        }
        changed = true;
        nextEntries.set(threadId, {
          shell: null,
          proposedPlanIds: undefined,
          proposedPlansById: undefined,
          entry: { id: threadId, proposedPlans: EMPTY_PROPOSED_PLANS },
        });
        continue;
      }

      const previous = previousEntries.get(threadId);
      if (
        previous &&
        previous.shell === shell &&
        previous.proposedPlanIds === proposedPlanIds &&
        previous.proposedPlansById === proposedPlansById
      ) {
        nextEntries.set(threadId, previous);
        nextResult.push(previous.entry);
        continue;
      }

      changed = true;
      const proposedPlans =
        proposedPlanIds && proposedPlanIds.length > 0 && proposedPlansById
          ? proposedPlanIds.flatMap((planId) => {
              const proposedPlan = proposedPlansById?.[planId];
              return proposedPlan ? [proposedPlan] : [];
            })
          : EMPTY_PROPOSED_PLANS;
      const entry = { id: threadId, proposedPlans };
      nextEntries.set(threadId, {
        shell,
        proposedPlanIds,
        proposedPlansById,
        entry,
      });
      nextResult.push(entry);
    }

    if (!changed && previousResult.length === nextResult.length) {
      return previousResult;
    }

    previousThreadIds = threadIds;
    previousEntries = nextEntries;
    previousResult = nextResult;
    return nextResult;
  };
}
